// src/components/StandardBolag.js

/**
 * StandardBolag Component
 *
 * This component represents a chat interface for the StandardBolag application.
 * It utilizes a chat service to communicate with a bot and display messages
 * exchanged between the user and the bot. The component is designed to be
 * responsive and visually appealing, adhering to the brand's color scheme.
 *
 * Props:
 * - botId (string): The unique identifier for the bot that the chat will
 *   communicate with.
 * - apiBaseUrl (string): The base URL for the API that the chat service
 *   will use to connect to the backend.
 * - customDisclaimerText (string): The custom disclaimer text to be displayed in the chat.
 * - initialMessage (string): The initial message to be displayed in the chat,
 *   which can be set based on URL parameters.
 * - initialMessageIsSilent (boolean): A flag indicating whether the initial message should be sent silently.
 * - chatContext (array): An array of context messages to be sent with the initial message.
 *
 * State Variables:
 * - userInitialMessage (string): The initial message to be sent to the bot,
 *   which can be set based on URL parameters or directly from props.
 * - messages (array): An array that holds the messages exchanged in the chat,
 *   including both user and bot messages.
 * - isSending (boolean): A flag indicating whether a message is currently
 *   being sent.
 *
 * Brand Colors:
 * The component defines a set of brand colors used for styling various
 * elements within the chat interface:
 * - primary: The primary color for the StandardBolag brand (green).
 * - secondary: A dark gray color used for text.
 * - text: The color used for general text in the chat.
 * - lightGray: A light gray color for backgrounds.
 * - border: The color used for borders around elements.
 * - white: The color white used for backgrounds and text.
 * - background: The background color for the chat interface.
 * - messageBg: The background color for AI messages.
 * - headerBg: The background color for the chat header.
 * - userMessageBg: The background color for user messages.
 * - shadow: A lighter shadow color for cleaner look.
 *
 * Avatars:
 * The component uses external APIs to generate avatars for the bot and user
 * based on their identifiers. The bot's avatar is generated with a green
 * background, while the user's avatar has a blue background.
 *
 * Effects:
 * The component uses the useEffect hook to retrieve an initial message from
 * the URL's query parameters when the component mounts. If a search field
 * is present in the URL, it sets that as the initial message.
 *
 * Message Handling:
 * The component includes a function to update the messages displayed in the
 * chat. This function allows for dynamic updates to the chat interface as
 * new messages are sent or received.
 *
 * Usage:
 * To use the StandardBolag component, it should be rendered within a parent
 * component that provides the required props (botId and apiBaseUrl). The
 * component will handle the chat functionality and display messages
 * accordingly.
 */

import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import gfm from "remark-gfm";
import Chat from "./Chat";
import { motion } from "framer-motion";
import { BotMessageSquare, Bot } from "lucide-react";
function StandardBolag({
    botId,
    apiBaseUrl,
    customDisclaimerText,
    initialMessage = "",
    initialMessageIsSilent = false,
    chatContext = [],
}) {
    const [userInitialMessage, setUserInitialMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const chatRef = useRef(null);
    const [isSending, setIsSending] = useState(false);
    const initialMessageSentRef = useRef(false);
    const containerRef = useRef(null);

    // Determine the correct API base URL
    const effectiveApiBaseUrl =
        apiBaseUrl ||
        (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1"
            ? "http://localhost:3000"
            : "https://beta.chattrick.witniumtech.com");

    // Add effect to adapt to container size
    useEffect(() => {
        // Find the target container with id 'chattrick'
        let container = document.getElementById("chattrick");

        // If no container found, create one as a fallback
        if (!container) {
            console.warn("No #chattrick container found. Creating one...");
            container = document.createElement("div");
            container.id = "chattrick";
            document.body.appendChild(container);
        }

        containerRef.current = container;

        // Apply necessary styles to adapt to the container WITHOUT changing its tag or structure
        container.style.overflow = "hidden";
        container.style.position = "relative";
        container.style.height = "600px";
        container.style.maxHeight = "600px";
        container.style.minHeight = "600px";
        container.style.display = "flex";
        container.style.flexDirection = "column";
        container.style.zIndex = "999";
    }, []);

    // Adapt branding to match StandardBolag's site
    // Update brand colors to match standardbolag.se
    const brandColors = {
        primary: "#4b6c9e", // StandardBolag blue
        secondary: "#333333", // Dark gray
        text: "#333333",
        lightGray: "#f8f9fa",
        border: "#e0e0e0",
        white: "#ffffff",
        background: "#FCFCFC",
        messageBg: "#FCFCFC",
        headerBg: "#F4F4F4",
        userMessageBg: "#4b6c9e", // Blue matching StandardBolag's theme
        shadow: "rgba(0, 0, 0, 0.05)",
    };

    // Constants
    // Using the complete URL including the protocol, as in the original file
    const botAvatar =
        "https://api.dicebear.com/7.x/initials/svg?seed=SB&backgroundColor=009550&bold=true";
    const userAvatar =
        "https://api.dicebear.com/7.x/initials/svg?seed=User&backgroundColor=0070db&bold=true";

    // Add mutation observer to adjust message container when header height changes
    useEffect(() => {
        const adjustMessageContainer = () => {
            const header = document.querySelector('.chattrick-container .text-dark');
            const messageContainer = document.querySelector('.messages-scroll-container');

            if (header && messageContainer) {
                const headerHeight = header.offsetHeight;
                messageContainer.style.top = `${headerHeight}px`;
            }
        };

        // Run initial adjustment
        adjustMessageContainer();

        // Set up mutation observer to watch for changes in the header's size
        const header = document.querySelector('.chattrick-container .text-dark');
        if (header) {
            const observer = new MutationObserver(adjustMessageContainer);
            observer.observe(header, {
                attributes: true,
                childList: true,
                subtree: true,
                characterData: true
            });

            // Also adjust on window resize
            window.addEventListener('resize', adjustMessageContainer);

            return () => {
                observer.disconnect();
                window.removeEventListener('resize', adjustMessageContainer);
            };
        }
    }, []);

    useEffect(() => {
        // Get initial message from the url query search_field if not provided via props
        if (!initialMessage) {
            const urlParams = new URLSearchParams(window.location.search);
            const searchField = urlParams.get("search_field");

            if (searchField && searchField.trim()) {
                setUserInitialMessage(searchField);
                console.log("Initial search query:", searchField);
            }
        } else {
            setUserInitialMessage(initialMessage);
        }
    }, [initialMessage]);

    // Send initial message when it's set and not yet sent
    useEffect(() => {
        const messageToSend = userInitialMessage || initialMessage;

        if (messageToSend && !initialMessageSentRef.current) {
            // Small delay to ensure chat is ready
            const timeoutId = setTimeout(() => {
                if (chatRef.current && chatRef.current.sendMessage) {
                    chatRef.current.sendMessage(
                        messageToSend,
                        !initialMessageIsSilent
                    ); // Add to history based on setting
                    initialMessageSentRef.current = true;
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [userInitialMessage, initialMessage, initialMessageIsSilent]);

    // Function to handle message updates
    const updateMessages = (msg, index) => {
        setMessages((prev) => {
            const newMessages = [...prev];
            newMessages[index] = msg;
            return newMessages;
        });

        // Force scroll after message updates
        setTimeout(() => {
            const scrollContainer = document.querySelector(
                ".messages-scroll-container"
            );
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }, 100);
    };

    // Typing animation component
    const TypingDots = () => (
        <div className="flex space-x-2 p-3 bg-gray-100 text-gray-800 rounded-lg">
            <div
                className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
            ></div>
            <div
                className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                style={{ animationDelay: "200ms" }}
            ></div>
            <div
                className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                style={{ animationDelay: "400ms" }}
            ></div>
        </div>
    );

    // Animation variants for messages with more modern transitions
    const messageAnimationVariants = {
        hidden: { opacity: 0, y: 10, scale: 0.98 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.3,
                ease: "easeOut",
            },
        },
    };

    // Custom renderer for user messages with modern styling
    const renderUserMessage = (msg, index) => {
        if (!messages[index]) {
            updateMessages(msg, index);
        }

        return (
            <motion.div
                key={index}
                className="flex justify-end items-end mb-4"
                initial="hidden"
                animate="visible"
                variants={messageAnimationVariants}
            >
                <div
                    className="p-3 rounded-2xl text-black max-w-[80%] shadow-sm chattrick-user-message-container"
                    style={{
                        backgroundColor: brandColors.headerBg,
                        marginRight: "8px",
                        border: "1px solid rgba(0, 0, 0, 0.05)",
                        fontSize: "14px",
                    }}
                >
                    <ReactMarkdown
                        remarkPlugins={[gfm]}
                        rehypePlugins={[rehypeSanitize]}
                    >
                        {msg.message}
                    </ReactMarkdown>
                </div>
            </motion.div>
        );
    };

    // Custom renderer for bot messages with modern styling
    const renderBotMessage = (msg, index) => {
        if (!messages[index]) {
            updateMessages(msg, index);
        }

        // Force scroll after bot messages render
        setTimeout(() => {
            const scrollContainer = document.querySelector(
                ".messages-scroll-container"
            );
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }, 100);

        return (
            <motion.div
                key={index}
                className="flex flex-col mb-5"
                initial="hidden"
                animate="visible"
                variants={messageAnimationVariants}
                onAnimationComplete={() => {
                    // Force scroll after animation completes
                    const scrollContainer = document.querySelector(
                        ".messages-scroll-container"
                    );
                    if (scrollContainer) {
                        scrollContainer.scrollTop =
                            scrollContainer.scrollHeight;
                    }
                }}
            >
                <div className="flex justify-start items-end chattrick-bot-message-container">
                    <div
                        className="px-4 py-3 rounded-2xl max-w-[80%] leading-relaxed shadow-sm"
                        style={{
                            color: "black",
                            backgroundColor: brandColors.background,
                            border: "1px solid #eeeeee",
                        }}
                    >
                        <ReactMarkdown
                            remarkPlugins={[gfm]}
                            rehypePlugins={[rehypeSanitize]}
                            components={{
                                a: ({ node, ...props }) => (
                                    console.log("props", props),
                                    <a
                                        {...props}
                                        href={props.href + "?utm_source=chattrick&utm_medium=search-page"}
                                        style={{ color: brandColors.primary }}
                                        className="font-medium underline hover:opacity-80"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                ),
                            }}
                        >
                            {msg.message}
                        </ReactMarkdown>
                    </div>
                </div>
            </motion.div>
        );
    };

    // Custom container renderer with modern styling
    const renderContainer = ({
        messages,
        currentResponse,
        input,
        chatState,
        chatContainerRef,
    }) => {
        const isTyping = chatState?.busy;
        const streamedResponse = currentResponse
            ? currentResponse.split("\n").filter((line) => line.trim())
            : [];

        // Use manual DOM manipulation for direct scrolling
        setTimeout(() => {
            const scrollContainer = document.querySelector(
                ".messages-scroll-container"
            );
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }, 50);

        return (
            <div
                className="rounded-lg flex flex-col chattrick-container"
                style={{ height: "100%", position: "relative" }}
            >
                <div
                    className="flex-1 overflow-hidden shadow-sm rounded-lg flex flex-col"
                    style={{
                        fontFamily:
                            "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        backgroundColor: "#fff",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    {/* Header - styled to match standardbolag.se */}
                    <div
                        className="p-4 text-dark"
                        style={{
                            backgroundColor: "#f8f9fa",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 10,
                            borderTopLeftRadius: "4px",
                            borderTopRightRadius: "4px",
                            boxShadow: `0 1px 3px ${brandColors.shadow}`,
                            borderBottom: "1px solid #e0e0e0",
                            padding: "10px",
                            minHeight: "50px",
                            height: "auto",
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <div className="leading-relaxed w-full">
                            <span
                                className="text-black text-xs sm:text-sm flex flex-wrap items-center justify-start"
                                style={{ fontFamily: "inherit" }}
                            >
                                Genom att använda denna AI-sökfunktion godkänner
                                du vår{" "}
                                <a
                                    href="https://www.standardbolag.se/om-oss/integritetspolicy/"
                                    className="text-blue-600 hover:underline ml-1"
                                >
                                    integritetspolicy
                                </a>
                            </span>
                        </div>
                    </div>

                    {/* Messages container - scrollable */}
                    <div
                        ref={chatContainerRef}
                        className="overflow-y-auto px-4 py-2 messages-scroll-container"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#ffffff",
                            position: "absolute",
                            left: 0,
                            right: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            padding: "16px",
                            top: "auto",
                            bottom: "72px",
                        }}
                    >
                        {/* Welcome message if no messages yet */}
                        {messages.length === 0 &&
                            !currentResponse &&
                            !isTyping && (
                                <motion.div
                                    className="flex justify-start items-center mb-5" // Changed items-end to items-center for vertical alignment
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <div
                                        className="px-4 py-3 rounded-lg max-w-[80%] leading-relaxed shadow-sm"
                                        style={{
                                            color: brandColors.text,
                                            backgroundColor:
                                                brandColors.messageBg,
                                            border: "1px solid #eeeeee",
                                        }}
                                    >

                                        <div className="flex flex-wrap items-center">
                                            <span>Hej, detta är Standardbolags AI-sökfunktion!</span>
                                            <Bot className="ml-1" size={16} />
                                        </div>
                                        <p className="m-0">
                                            Skriv kort vad du vill veta mer om så försöker jag guida dig till rätt information.
                                        </p>

                                    </div>
                                </motion.div>
                            )}

                        {/* Chat messages */}
                        <div className="flex-1 space-y-4">
                            {messages}
                            {currentResponse && streamedResponse.length > 0 && (
                                <motion.div
                                    className="flex justify-start items-end mb-5"
                                    initial="hidden"
                                    animate="visible"
                                    variants={messageAnimationVariants}
                                >
                                    <div
                                        className="px-4 py-3 rounded-lg max-w-[80%] leading-relaxed shadow-sm"
                                        style={{
                                            color: brandColors.text,
                                            backgroundColor:
                                                brandColors.messageBg,
                                            border: "1px solid #eeeeee",
                                        }}
                                    >
                                        {streamedResponse.map((line, i) => (
                                            <ReactMarkdown
                                                key={i}
                                                remarkPlugins={[gfm]}
                                                rehypePlugins={[rehypeSanitize]}
                                            >
                                                {line}
                                            </ReactMarkdown>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                            {isTyping && !streamedResponse.length && (
                                <motion.div
                                    className="flex justify-start mb-5"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div
                                        className="px-4 py-3 rounded-lg shadow-sm"
                                        style={{
                                            backgroundColor:
                                                brandColors.messageBg,
                                            border: "1px solid #eeeeee",
                                        }}
                                    >
                                        <div className="flex space-x-2">
                                            <div
                                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                                style={{
                                                    animationDelay: "0ms",
                                                }}
                                            ></div>
                                            <div
                                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                                style={{
                                                    animationDelay: "200ms",
                                                }}
                                            ></div>
                                            <div
                                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                                style={{
                                                    animationDelay: "400ms",
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Input - fixed at bottom with modern styling */}
                    <div
                        style={{
                            backgroundColor: brandColors.white,
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 30,
                            padding: "12px 16px",
                            borderTop: "1px solid #f0f0f0",
                            height: "72px",
                        }}
                    >
                        {input}
                    </div>
                </div>
            </div>
        );
    };

    // Custom input renderer with styling to match standardbolag.se
    const renderInput = ({ value, onChange, onSend, disabled, ref }) => {
        return (
            <div className="flex items-center chattrick-input-container">
                <div
                    className="flex-grow relative flex items-center w-full border rounded-full overflow-hidden"
                    style={{
                        borderColor: disabled ? "#e5e5e5" : "#ddd",
                        boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                        background: disabled ? "#f5f5f5" : brandColors.white,
                        transition: "all 0.2s ease",
                        minHeight: "48px",
                    }}
                >
                    <input
                        ref={ref}
                        type="text"
                        className="w-full px-4 py-3 resize-none focus:outline-none"
                        style={{
                            fontFamily: "inherit",
                            color: brandColors.text,
                            fontSize: "14px",
                            backgroundColor: "transparent",
                            border: "none",
                            height: "48px",
                        }}
                        placeholder="Sök på webbsidan"
                        value={value}
                        onChange={onChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                onSend();
                            }
                        }}
                        disabled={disabled}
                        autoFocus={!disabled}
                    />
                    <button
                        onClick={() => {
                            setIsSending(true);
                            onSend();
                            setTimeout(() => setIsSending(false), 300);
                        }}
                        disabled={disabled || !value.trim()}
                        className="absolute right-4 flex items-center justify-center hover:opacity-90 hover:cursor-pointer  disabled:opacity-50 transition-all duration-200"
                        style={{
                            background: "transparent",
                            color: "#4b6c9e",
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faPaperPlane}
                            className="h-4 w-4 text-gray-500 hover:text-gray-700"
                        />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            className="h-full w-full flex flex-col overflow-hidden rounded-lg"
            ref={containerRef}
        >
            <Chat
                ref={chatRef}
                apiBaseUrl={effectiveApiBaseUrl}
                botId={botId}
                initialMessage=""
                initialMessageIsSilent={initialMessageIsSilent}
                chatContext={chatContext}
                renderUserMessage={renderUserMessage}
                renderBotMessage={renderBotMessage}
                renderInput={renderInput}
                renderContainer={renderContainer}
                persistConnection={true}
                showPrivacyBanner={false}
                customDisclaimerText={customDisclaimerText}
            />
        </div>
    );
}



export default StandardBolag;
